import { useDebounceCallback } from '@/hooks/state/use-debounce-callback';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { useAnalytics } from '@/services/analytics';
import { Step, type MergeSummary, type SelectedCustomers } from './types';

/** How long a person may pause typing an attribute search before it is recorded. */
const ATTRIBUTE_SEARCH_TRACKING_DELAY_MS = 2000;

/** The analytics label for each `source` an inbound link carries. */
const SOURCE_LABELS = new Map([
  ['people_list', 'People List Page'],
  ['people_options_dropdown', 'People Options Drop-down'],
  ['merge_success_banner', 'Merge Success Banner'],
  ['review_and_confirm_tab', 'Review and Confirm Tab'],
]);

const STEP_LABELS = {
  [Step.Setup]: 'Setup Tab',
  [Step.Preview]: 'Review and Confirm Tab',
} satisfies Record<Step, string>;

type WizardState = {
  /** Which step the wizard is on, which every search event is attributed to. */
  step: Step;
  selected: SelectedCustomers;
  primaryId: string | null;
  secondaryId: string | null;
  mergeSummary: MergeSummary | undefined;
  /** The `source` the inbound link carried, if any. */
  source: string | null;
};

/** Whether each side of the pair is filled, the shape both setup-tab events report. */
function pairSelection(selected: SelectedCustomers) {
  return {
    'Primary Profile Selected': selected.primary != null,
    'Secondary Profile Selected': selected.secondary != null,
  };
}

/**
 * The wizard's analytics. The page-view event fires once the pair has resolved
 * (so it can report whether either side arrived pre-filled), and the attribute
 * search is recorded only after typing settles.
 */
export function useMergeAnalytics(state: WizardState, isReady: boolean) {
  const { track } = useAnalytics();
  const { step, selected, primaryId, secondaryId, mergeSummary, source } = state;

  usePageViewTracking(
    () => {
      void track('people', 'mergeSetupTabViewed', {
        Source: source != null ? SOURCE_LABELS.get(source) : undefined,
        ...pairSelection(selected),
      });
    },
    isReady,
    { ignoreSearch: true },
  );

  const trackAttributeSearch = useDebounceCallback((term: string) => {
    void track('people', 'mergeAttributesSearched', {
      Source: STEP_LABELS[step],
      'Attribute Search Value': term,
    });
  }, ATTRIBUTE_SEARCH_TRACKING_DELAY_MS);

  const trackSwap = () => void track('people', 'mergePeopleSwapped', {});

  const trackFeedbackRequest = () =>
    void track('people', 'mergeFeedbackRequestStarted', {
      Source: STEP_LABELS[Step.Preview],
      'Feedback Type': undefined,
    });

  const trackReturnToSetup = () =>
    void track('people', 'mergeSetupTabViewed', {
      Source: SOURCE_LABELS.get('review_and_confirm_tab'),
      ...pairSelection(selected),
    });

  /**
   * The review step's counts. The summary reports each profile's attribute
   * total by id plus how many the merge adds, so what the secondary loses is
   * everything it holds that the merge does not carry over.
   */
  const trackReviewStep = () => {
    const added = mergeSummary?.attributes.added;
    const secondaryCount = secondaryId != null ? mergeSummary?.attributes[secondaryId] : undefined;
    void track('people', 'mergeReviewAndConfirmTabViewed', {
      'Primary Attribute Count': primaryId != null ? mergeSummary?.attributes[primaryId] : undefined,
      'Secondary Attribute Count (Added)': added,
      'Secondary Attribute Count (Lost)': secondaryCount != null && added != null ? secondaryCount - added : undefined,
    });
  };

  /** The step-change events, which differ by direction. */
  const trackStepChange = (next: Step) => {
    if (step === Step.Preview && next === Step.Setup) {
      trackReturnToSetup();
    } else if (step === Step.Setup && next === Step.Preview) {
      trackReviewStep();
    }
  };

  return { trackAttributeSearch, trackSwap, trackFeedbackRequest, trackStepChange };
}
